import React, { useState, useEffect, useCallback, useMemo } from 'react';
import FormModal from '../../components/Modal/FormModal';
import EntityList, { type EntityItem } from '../../components/admin/dataManagement/EntityList';
import DataManagementFilters from '../../components/admin/dataManagement/DataManagementFilters';
import DataManagementPagination from '../../components/admin/dataManagement/DataManagementPagination';
import ProductTypeRuleModal from '../../components/admin/dataManagement/ProductTypeRuleModal';
import {
  brandService,
  brandCategoryService,
  cityService,
  conditionService,
  districtService,
  listingComplaintProcessService,
  listingComplaintTypeService,
  neighborhoodService,
  notificationTypeService,
  policyService,
  productTypeService,
  secondaryCategoryService,
  specConstraintService,
  statusService,
  topCategoryService,
  warrantyService,
} from '../../api';
import { SpecValueType } from '../../api/types/product-type/SpecConstraintViewModel';
import type { SpecAllowedValueViewModel } from '../../api/types/product-type/SpecConstraintViewModel';
import type { CityViewModel } from '../../api/types/city';
import type { DistrictViewModel } from '../../api/types/district';
import type { TopCategoryViewModel } from '../../api/types/top-category';
import type { SecondaryCategoryViewModel } from '../../api/types/secondary-category';
import type { ProductTypeViewModel } from '../../api/types/product-type';
import type { BrandViewModel } from '../../api/types/brand';
import PolicyRichTextEditor from '../../components/admin/PolicyRichTextEditor';
import { policyTextForEditor } from '../../utils/policyContentHtml';
import type {
  SpecConstraintCreateModel,
  SpecConstraintUpdateModel,
} from '../../api/types/spec-constraint/SpecConstraintModels';
import { normalizeSpecConstraintsForAdmin, normalizeSpecValueType } from '../../utils/specConstraintUtils';
import { readFriendlyApiErrorMessage } from '../../utils/apiErrorMessages';
import {
  buildPaginatedEntityQuery,
  DEFAULT_DATA_MANAGEMENT_PAGE_SIZE,
  getActiveDataManagementFilterCount,
  hasActiveDataManagementFilters,
  isPaginatedDataEntity,
  parseEntityList,
  readPaginationMeta,
  type DataManagementSortOption,
  type PaginatedDataEntityKey,
} from '../../utils/dataManagementListUtils';

type EntityKey =
  | 'BrandCategory'
  | 'Brand'
  | 'City'
  | 'Condition'
  | 'District'
  | 'Neighborhood'
  | 'ListingComplaintProcess'
  | 'ListingComplaintType'
  | 'NotificationType'
  | 'Policy'
  | 'ProductType'
  | 'SecondaryCategory'
  | 'Status'
  | 'TopCategory'
  | 'Warranty'
  | 'SpecConstraint';

type EntityItemExtended = EntityItem & {
  text?: string | null;
  title?: string | null;
  message?: string | null;
  slug?: string | null;
  brandId?: number;
  productTypeId?: number;
  cityId?: number;
  districtId?: number;
  topCategoryId?: number;
  secondaryCategoryId?: number;
  key?: string;
  displayName?: string;
  valueType?: SpecValueType;
  allowedValues?: SpecAllowedValueViewModel[] | null;
  productTypes?: ProductTypeViewModel[] | null;
  productTypeIds?: number[] | null;
  isDeleted?: boolean;
  affectedRoles?: string[] | null;
  contentFormat?: number | null;
};

const ENTITY_LABELS: Record<EntityKey, string> = {
  BrandCategory: 'Marka Kategorisi',
  Brand: 'Marka',
  City: 'Şehir',
  Condition: 'Kondisyon',
  District: 'İlçe',
  Neighborhood: 'Mahalle',
  ListingComplaintProcess: 'İlan Şikayet Süreci',
  ListingComplaintType: 'İlan Şikayet Tipi',
  NotificationType: 'Bildirim Tipi',
  Policy: 'Sözleşmeler',
  ProductType: 'Ürün Tipi',
  SecondaryCategory: 'Alt Kategori',
  Status: 'Durum',
  TopCategory: 'Üst Kategori',
  Warranty: 'Garanti',
  SpecConstraint: 'Özellik',
};

const POLICY_ROLE_LABELS: Record<string, string> = {
  User: 'Bireysel kullanıcı (User)',
  'Corporate User': 'Kurumsal kullanıcı (Corporate User)',
};

function getSpecValueTypeLabel(valueType: SpecValueType | number | string | undefined): string {
  if (valueType === SpecValueType.Number || valueType === 0 || valueType === 'Number') return 'Sayı';
  if (valueType === SpecValueType.String || valueType === 1 || valueType === 'String') return 'Metin';
  if (valueType === SpecValueType.Enum || valueType === 2 || valueType === 'Enum') return 'Seçenek listesi';
  return valueType != null ? String(valueType) : '';
}

function getSpecProductTypeLabel(item: EntityItemExtended): string {
  const names = item.productTypes
    ?.map((pt) => pt.name)
    .filter((name): name is string => name != null && name !== '');
  if (names && names.length > 0) return names.join(', ');
  return 'Atanmamış';
}

function getDisplayName(item: EntityItemExtended): string {
  if (item.displayName != null && item.displayName !== '' && item.key != null && item.key !== '') {
    return `${item.displayName} (${item.key}) — ${getSpecValueTypeLabel(item.valueType)} · ${getSpecProductTypeLabel(item)}`;
  }
  if (item.name != null && item.name !== '') return String(item.name);
  if ('text' in item && item.text != null && item.text !== '') return String(item.text);
  if ('title' in item && item.title != null && item.title !== '') return String(item.title);
  if ('brandId' in item && 'productTypeId' in item)
    return `Marka ${item.brandId} - Ürün tipi ${item.productTypeId}`;
  return `#${item.id}`;
}

const SOFT_DELETABLE_ENTITIES = [
  'TopCategory',
  'SecondaryCategory',
  'ProductType',
  'SpecConstraint',
] as const;

type SoftDeletableEntityKey = (typeof SOFT_DELETABLE_ENTITIES)[number];

const dropdownListParams = { pagination: { pageSize: 500, pageNumber: 1 } };

const DataManagementPage: React.FC = () => {
  const [entityKey, setEntityKey] = useState<EntityKey>('Status');
  const [items, setItems] = useState<EntityItemExtended[]>([]);
  const [deletedItems, setDeletedItems] = useState<EntityItemExtended[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_DATA_MANAGEMENT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [appliedSearchText, setAppliedSearchText] = useState('');
  const [parentFilterId, setParentFilterId] = useState<number | undefined>(undefined);
  const [sort, setSort] = useState<DataManagementSortOption>('name_asc');
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<EntityItemExtended | null>(null);
  const [formState, setFormState] = useState<{
    name?: string;
    text?: string;
    title?: string;
    message?: string;
    slug?: string;
    cityId?: number;
    districtId?: number;
    topCategoryId?: number;
    secondaryCategoryId?: number;
    brandId?: number;
    productTypeId?: number;
    specKey?: string;
    specDisplayName?: string;
    valueType?: SpecValueType;
    allowedValues?: string[];
    affectedRoles?: string[];
  }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [assignablePolicyRoles, setAssignablePolicyRoles] = useState<string[]>([]);
  const [allowedValueDragIndex, setAllowedValueDragIndex] = useState<number | null>(null);

  const [cities, setCities] = useState<CityViewModel[]>([]);
  const [districts, setDistricts] = useState<DistrictViewModel[]>([]);
  const [topCategories, setTopCategories] = useState<TopCategoryViewModel[]>([]);
  const [secondaryCategories, setSecondaryCategories] = useState<SecondaryCategoryViewModel[]>([]);
  const [productTypes, setProductTypes] = useState<ProductTypeViewModel[]>([]);
  const [brands, setBrands] = useState<BrandViewModel[]>([]);

  useEffect(() => {
    if (entityKey !== 'Policy') return;
    policyService.getAssignablePolicyRoles().then((r) => {
      if (r.isSuccess && r.data) setAssignablePolicyRoles(r.data);
    });
  }, [entityKey]);

  useEffect(() => {
    cityService.getAll({ pagination: { pageSize: 500, pageNumber: 1 } }).then((r) => {
      if (r.isSuccess && r.data) setCities(r.data);
    });
    districtService.getAll({}).then((r) => {
      if (r.isSuccess && r.data) setDistricts(r.data);
    });
    topCategoryService.getAll(dropdownListParams).then((r) => {
      if (r.isSuccess && r.data) setTopCategories(parseEntityList(r.data) as TopCategoryViewModel[]);
    });
    secondaryCategoryService.getAll(dropdownListParams).then((r) => {
      if (r.isSuccess && r.data) setSecondaryCategories(parseEntityList(r.data) as SecondaryCategoryViewModel[]);
    });
    productTypeService.getAll(dropdownListParams).then((r) => {
      if (r.isSuccess && r.data) setProductTypes(parseEntityList(r.data) as ProductTypeViewModel[]);
    });
    brandService.getAll({}).then((r) => {
      if (r.isSuccess && r.data) setBrands(r.data);
    });
  }, []);

  const refreshProductTypes = useCallback(() => {
    productTypeService.getAll(dropdownListParams).then((r) => {
      if (r.isSuccess && r.data) setProductTypes(parseEntityList(r.data) as ProductTypeViewModel[]);
    });
  }, []);

  const refreshTopCategories = useCallback(() => {
    topCategoryService.getAll(dropdownListParams).then((r) => {
      if (r.isSuccess && r.data) setTopCategories(parseEntityList(r.data) as TopCategoryViewModel[]);
    });
  }, []);

  const refreshSecondaryCategories = useCallback(() => {
    secondaryCategoryService.getAll(dropdownListParams).then((r) => {
      if (r.isSuccess && r.data) setSecondaryCategories(parseEntityList(r.data) as SecondaryCategoryViewModel[]);
    });
  }, []);

  const refreshCities = useCallback(() => {
    cityService.getAll({ pagination: { pageSize: 500, pageNumber: 1 } }).then((r) => {
      if (r.isSuccess && r.data) setCities(r.data);
    });
  }, []);

  const refreshBrands = useCallback(() => {
    brandService.getAll({}).then((r) => {
      if (r.isSuccess && r.data) setBrands(r.data);
    });
  }, []);

  const refreshRelatedDropdowns = useCallback((key: EntityKey) => {
    switch (key) {
      case 'TopCategory':
        refreshTopCategories();
        break;
      case 'SecondaryCategory':
        refreshSecondaryCategories();
        break;
      case 'ProductType':
        refreshProductTypes();
        break;
      case 'City':
        refreshCities();
        break;
      case 'Brand':
        refreshBrands();
        break;
      default:
        break;
    }
  }, [refreshTopCategories, refreshSecondaryCategories, refreshProductTypes, refreshCities, refreshBrands]);

  const loadSoftDeletableItems = useCallback((key: SoftDeletableEntityKey): Promise<void> => {
    const isPaginated = isPaginatedDataEntity(key);
    const activeQuery = isPaginated
      ? buildPaginatedEntityQuery(key, {
          pageNumber,
          pageSize,
          searchText: appliedSearchText,
          parentFilterId,
          sort,
        })
      : dropdownListParams;
    const deletedQuery = isPaginated
      ? buildPaginatedEntityQuery(key, {
          pageNumber: 1,
          pageSize: 50,
          searchText: appliedSearchText,
          parentFilterId,
          sort,
          isDeleted: true,
        })
      : { ...dropdownListParams, isDeleted: true };

    const loaders = {
      TopCategory: async () => {
        const [activeRes, deletedRes] = await Promise.all([
          topCategoryService.getAll(activeQuery),
          topCategoryService.getAll(deletedQuery),
        ]);
        return { activeRes, deletedRes };
      },
      SecondaryCategory: async () => {
        const [activeRes, deletedRes] = await Promise.all([
          secondaryCategoryService.getAll(activeQuery),
          secondaryCategoryService.getAll(deletedQuery),
        ]);
        return { activeRes, deletedRes };
      },
      ProductType: async () => {
        const [activeRes, deletedRes] = await Promise.all([
          productTypeService.getAll(activeQuery),
          productTypeService.getAll(deletedQuery),
        ]);
        return { activeRes, deletedRes };
      },
      SpecConstraint: async () => {
        const [activeRes, deletedRes] = await Promise.all([
          specConstraintService.getAll(activeQuery),
          specConstraintService.getAll(deletedQuery),
        ]);
        return { activeRes, deletedRes };
      },
    };

    setLoading(true);
    setListError(null);
    setDeletedItems([]);

    return loaders[key]()
      .then(({ activeRes, deletedRes }) => {
        if (!activeRes.isSuccess) {
          setItems([]);
          if (activeRes.message) setListError(activeRes.message);
          setTotalPages(1);
          return;
        }

        const activeItems = activeRes.data ? parseEntityList<EntityItemExtended>(activeRes.data) : [];
        const deletedList = deletedRes.isSuccess && deletedRes.data
          ? parseEntityList<EntityItemExtended>(deletedRes.data)
          : [];

        if (isPaginated) {
          const paginationMeta = readPaginationMeta(activeRes.pagination, pageSize);
          setTotalPages(paginationMeta.totalPages);
        }

        const normalizedActive =
          key === 'SpecConstraint'
            ? normalizeSpecConstraintsForAdmin(activeItems).map((item) => ({
                ...item,
                valueType: normalizeSpecValueType(item.valueType),
              }))
            : activeItems;
        const normalizedDeleted =
          key === 'SpecConstraint'
            ? normalizeSpecConstraintsForAdmin(deletedList).map((item) => ({
                ...item,
                valueType: normalizeSpecValueType(item.valueType),
              }))
            : deletedList;
        setItems(normalizedActive);
        setDeletedItems(normalizedDeleted);
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Liste yenilenemedi.';
        setListError(message);
      })
      .finally(() => setLoading(false));
  }, [appliedSearchText, pageNumber, pageSize, parentFilterId, sort]);

  const loadItems = useCallback((): Promise<void> => {
    if ((SOFT_DELETABLE_ENTITIES as readonly EntityKey[]).includes(entityKey)) {
      return loadSoftDeletableItems(entityKey as SoftDeletableEntityKey);
    }

    setLoading(true);
    setListError(null);
    setDeletedItems([]);

    type StandardEntityKey = Exclude<EntityKey, SoftDeletableEntityKey>;

    const promises: Record<
      StandardEntityKey,
      () => Promise<{ data?: unknown[] | null; isSuccess: boolean; message?: string | null }>
    > = {
      Brand: () => brandService.getAll({}),
      BrandCategory: () => brandCategoryService.getAll({}),
      City: () => cityService.getAll({ pagination: { pageSize: 500, pageNumber: 1 } }),
      Condition: () => conditionService.getAll(),
      District: () => districtService.getAll({}),
      Neighborhood: () => neighborhoodService.getAll({}),
      ListingComplaintProcess: () => listingComplaintProcessService.getAll({}),
      ListingComplaintType: () => listingComplaintTypeService.getAll({}),
      NotificationType: () => notificationTypeService.getAll({}),
      Policy: () => policyService.getAll({ pagination: { pageSize: 500, pageNumber: 1 } }),
      Status: () => statusService.getAll({}),
      Warranty: () => warrantyService.getAll(),
    };
    return promises[entityKey as StandardEntityKey]()
      .then((res) => {
        if (!res.isSuccess || !res.data) {
          setItems([]);
          if (!res.isSuccess && res.message) setListError(res.message);
          return;
        }
        setItems(parseEntityList(res.data));
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Liste yenilenemedi.';
        setListError(message);
      })
      .finally(() => setLoading(false));
  }, [entityKey, loadSoftDeletableItems]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setAppliedSearchText(searchText);
      setPageNumber(1);
    }, 400);
    return () => window.clearTimeout(handle);
  }, [searchText]);

  const parentFilterOptions = useMemo(() => {
    if (entityKey === 'SecondaryCategory') {
      return topCategories.map((c) => ({
        value: Number(c.id),
        label: c.name ?? String(c.id),
      }));
    }
    if (entityKey === 'ProductType') {
      return secondaryCategories.map((c) => ({
        value: Number(c.id),
        label: c.name ?? String(c.id),
      }));
    }
    if (entityKey === 'SpecConstraint') {
      return productTypes.map((p) => ({
        value: Number(p.id),
        label: p.name ?? String(p.id),
      }));
    }
    return [];
  }, [entityKey, topCategories, secondaryCategories, productTypes]);

  const showPaginatedControls = isPaginatedDataEntity(entityKey);
  const hasActiveFilters = hasActiveDataManagementFilters(searchText, parentFilterId, sort);
  const activeFilterCount = getActiveDataManagementFilterCount(searchText, parentFilterId, sort);

  const handleEntityKeyChange = (nextKey: EntityKey) => {
    setEntityKey(nextKey);
    setPageNumber(1);
    setPageSize(DEFAULT_DATA_MANAGEMENT_PAGE_SIZE);
    setSearchText('');
    setAppliedSearchText('');
    setParentFilterId(undefined);
    setSort('name_asc');
    setTotalPages(1);
    setFiltersExpanded(true);
  };

  const handleAddClick = () => {
    setEditingItem(null);
    setFormState(
      entityKey === 'SpecConstraint'
        ? { valueType: SpecValueType.Number, allowedValues: [''] }
        : entityKey === 'Policy'
          ? { affectedRoles: [...assignablePolicyRoles] }
          : {}
    );
    setSubmitError(null);
    setShowForm(true);
  };

  const handleUpdateClick = (item: EntityItemExtended) => {
    setEditingItem(item);
    if (entityKey === 'SpecConstraint') {
      const normalizedType = normalizeSpecValueType(item.valueType);
      const enumValues =
        item.allowedValues && item.allowedValues.length > 0
          ? item.allowedValues.map((av) => av.value)
          : normalizedType === SpecValueType.Enum
            ? ['']
            : [];
      setFormState({
        specKey: item.key != null ? String(item.key) : '',
        specDisplayName: item.displayName != null ? String(item.displayName) : '',
        valueType: normalizeSpecValueType(item.valueType ?? SpecValueType.Number),
        productTypeId: item.productTypeId ?? item.productTypeIds?.[0] ?? item.productTypes?.[0]?.id,
        allowedValues: enumValues,
      });
    } else {
      const rawText = 'text' in item && item.text != null ? String(item.text) : '';
      const policyText =
        entityKey === 'Policy'
          ? policyTextForEditor(rawText, item.contentFormat)
          : rawText;
      setFormState({
        name: item.name != null ? String(item.name) : '',
        text: policyText,
        title: 'title' in item && item.title != null ? String(item.title) : '',
        message: 'message' in item && item.message != null ? String(item.message) : '',
        slug: 'slug' in item && item.slug != null ? String(item.slug) : '',
        affectedRoles: 'affectedRoles' in item && item.affectedRoles ? [...item.affectedRoles] : [],
        cityId: 'cityId' in item ? item.cityId : undefined,
        districtId: 'districtId' in item ? item.districtId : undefined,
        topCategoryId: 'topCategoryId' in item ? item.topCategoryId : undefined,
        secondaryCategoryId: 'secondaryCategoryId' in item ? item.secondaryCategoryId : undefined,
        brandId: 'brandId' in item ? item.brandId : undefined,
        productTypeId: 'productTypeId' in item ? item.productTypeId : undefined,
      });
    }
    setSubmitError(null);
    setShowForm(true);
  };

  const buildPayload = (): Record<string, unknown> => {
    const key = entityKey;
    if (
      key === 'Brand' ||
      key === 'Status' ||
      key === 'Condition' ||
      key === 'Warranty' ||
      key === 'TopCategory' ||
      key === 'City' ||
      key === 'ListingComplaintType'
    ) {
      return { name: formState.name || undefined };
    }
    if (key === 'ListingComplaintProcess') {
      return { text: formState.text || undefined };
    }
    if (key === 'NotificationType') {
      return { title: formState.title || undefined, message: formState.message || undefined };
    }
    if (key === 'Policy') {
      return {
        name: formState.name || undefined,
        slug: formState.slug || undefined,
        text: formState.text || undefined,
        affectedRoles: formState.affectedRoles?.length ? formState.affectedRoles : undefined,
      };
    }
    if (key === 'District') {
      return { name: formState.name || undefined, cityId: formState.cityId ?? 0 };
    }
    if (key === 'Neighborhood') {
      return { name: formState.name || undefined, districtId: formState.districtId ?? 0 };
    }
    if (key === 'SecondaryCategory') {
      return { name: formState.name || undefined, topCategoryId: formState.topCategoryId ?? 0 };
    }
    if (key === 'ProductType') {
      return { name: formState.name || undefined, secondaryCategoryId: formState.secondaryCategoryId ?? 0 };
    }
    if (key === 'BrandCategory') {
      return { brandId: formState.brandId ?? 0, productTypeId: formState.productTypeId ?? 0 };
    }
    if (key === 'SpecConstraint') {
      const valueType = formState.valueType ?? SpecValueType.Number;
      const allowedValues =
        valueType === SpecValueType.Enum
          ? (formState.allowedValues ?? []).map((v) => v.trim()).filter((v) => v !== '')
          : undefined;
      return {
        key: formState.specKey?.trim(),
        displayName: formState.specDisplayName?.trim(),
        valueType,
        productTypeId: formState.productTypeId ?? 0,
        allowedValues,
      };
    }
    return {};
  };

  const validateSpecForm = (): string | null => {
    if (entityKey !== 'SpecConstraint') return null;
    if (!formState.specKey?.trim()) return 'Anahtar gereklidir.';
    if (!formState.specDisplayName?.trim()) return 'Görünen ad gereklidir.';
    if (!formState.productTypeId) return 'Ürün tipi gereklidir.';
    if (formState.valueType == null) return 'Değer tipi gereklidir.';
    if (formState.valueType === SpecValueType.Enum) {
      const vals = (formState.allowedValues ?? []).map((v) => v.trim()).filter((v) => v !== '');
      if (vals.length === 0) return 'Seçenek listesi tipi için en az bir izin verilen değer gereklidir.';
    }
    return null;
  };

  const handleFormAccept = async () => {
    setSubmitError(null);
    const validationError = validateSpecForm();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }
    const payload = buildPayload();
    try {
      let res: { isSuccess: boolean; message?: string | null; data?: unknown };
      if (editingItem) {
        const updateFns: Record<EntityKey, (id: number | string, p: unknown) => Promise<{ isSuccess: boolean; message?: string | null }>> = {
          Brand: (id, p) => brandService.update(id, p as { name?: string | null }),
          BrandCategory: (id, p) => brandCategoryService.update(id, p as { brandId?: number | null; productTypeId?: number | null }),
          City: (id, p) => cityService.update(id, p as { name?: string | null }),
          Condition: (id, p) => conditionService.update(id, p as { name?: string | null }),
          District: (id, p) => districtService.update(id, p as { name?: string | null; cityId?: number | null }),
          Neighborhood: (id, p) => neighborhoodService.update(id, p as { name?: string | null; districtId?: number | null }),
          ListingComplaintProcess: (id, p) => listingComplaintProcessService.update(id, p as { text?: string | null }),
          ListingComplaintType: (id, p) => listingComplaintTypeService.update(id, p as { name?: string | null }),
          NotificationType: (id, p) => notificationTypeService.update(id, p as { title?: string | null; message?: string | null }),
          Policy: (id, p) => policyService.update(id, p as { name?: string | null; slug?: string | null; text?: string | null; affectedRoles?: string[] | null }),
          ProductType: (id, p) => productTypeService.update(id, p as { name?: string | null; secondaryCategoryId?: number | null }),
          SecondaryCategory: (id, p) => secondaryCategoryService.update(id, p as { name?: string | null; topCategoryId?: number | null }),
          Status: (id, p) => statusService.update(id, p as { name?: string | null }),
          TopCategory: (id, p) => topCategoryService.update(id, p as { name?: string | null }),
          Warranty: (id, p) => warrantyService.update(id, p as { name?: string | null }),
          SpecConstraint: (id, p) => specConstraintService.update(id, p as SpecConstraintUpdateModel),
        };
        res = await updateFns[entityKey](editingItem.id, payload);
      } else {
        const createFns: Record<EntityKey, (p: unknown) => Promise<{ isSuccess: boolean; message?: string | null }>> = {
          Brand: (p) => brandService.create(p as { name?: string | null }),
          BrandCategory: (p) => brandCategoryService.create(p as { brandId: number; productTypeId: number }),
          City: (p) => cityService.create(p as { name?: string | null }),
          Condition: (p) => conditionService.create(p as { name?: string | null }),
          District: (p) => districtService.create(p as { name?: string | null; cityId: number }),
          Neighborhood: (p) => neighborhoodService.create(p as { name?: string | null; districtId: number }),
          ListingComplaintProcess: (p) => listingComplaintProcessService.create(p as { text?: string | null }),
          ListingComplaintType: (p) => listingComplaintTypeService.create(p as { name?: string | null }),
          NotificationType: (p) => notificationTypeService.create(p as { title?: string | null; message?: string | null }),
          Policy: (p) => policyService.create(p as { name?: string | null; slug?: string | null; text?: string | null; affectedRoles?: string[] | null }),
          ProductType: (p) => productTypeService.create(p as { name?: string | null; secondaryCategoryId: number }),
          SecondaryCategory: (p) => secondaryCategoryService.create(p as { name?: string | null; topCategoryId: number }),
          Status: (p) => statusService.create(p as { name?: string | null }),
          TopCategory: (p) => topCategoryService.create(p as { name?: string | null }),
          Warranty: (p) => warrantyService.create(p as { name?: string | null }),
          SpecConstraint: (p) => specConstraintService.create(p as SpecConstraintCreateModel),
        };
        res = await createFns[entityKey](payload);
      }
      if (res.isSuccess) {
        setShowForm(false);
        if (!editingItem && res.data && typeof res.data === 'object' && 'id' in res.data) {
          const created = res.data as EntityItemExtended;
          setItems((prev) => {
            if (prev.some((item) => item.id === created.id)) return prev;
            if (entityKey === 'SpecConstraint') {
              const normalized = normalizeSpecConstraintsForAdmin([created])[0];
              return [...prev, { ...normalized, valueType: normalizeSpecValueType(normalized.valueType) }];
            }
            return [...prev, created];
          });
        }
        refreshRelatedDropdowns(entityKey);
        await loadItems();
      } else {
        setSubmitError(res.message || 'İşlem başarısız.');
      }
    } catch (err: unknown) {
      setSubmitError(readFriendlyApiErrorMessage(err, 'İstek başarısız.'));
    }
  };

  const handleDelete = async (item: EntityItemExtended) => {
    setListError(null);
    const deleteFns: Record<EntityKey, (id: number | string) => Promise<{ isSuccess: boolean; message?: string | null }>> = {
      Brand: (id) => brandService.delete(id),
      BrandCategory: (id) => brandCategoryService.delete(id),
      City: (id) => cityService.delete(id),
      Condition: (id) => conditionService.delete(id),
      District: (id) => districtService.delete(id),
      Neighborhood: (id) => neighborhoodService.delete(id),
      ListingComplaintProcess: (id) => listingComplaintProcessService.delete(id),
      ListingComplaintType: (id) => listingComplaintTypeService.delete(id),
      NotificationType: (id) => notificationTypeService.delete(id),
      Policy: (id) => policyService.delete(id),
      ProductType: (id) => productTypeService.delete(id),
      SecondaryCategory: (id) => secondaryCategoryService.delete(id),
      Status: (id) => statusService.delete(id),
      TopCategory: (id) => topCategoryService.delete(id),
      Warranty: (id) => warrantyService.delete(id),
      SpecConstraint: (id) => specConstraintService.delete(id),
    };
    const res = await deleteFns[entityKey](item.id);
    if (res.isSuccess) {
      await loadItems();
    } else {
      setListError(res.message || 'Silme işlemi başarısız.');
    }
  };

  const renderFormFields = () => {
    const key = entityKey;
    const nameOnly =
      key === 'Brand' ||
      key === 'Status' ||
      key === 'Condition' ||
      key === 'Warranty' ||
      key === 'TopCategory' ||
      key === 'City' ||
      key === 'ListingComplaintType';
    if (nameOnly) {
      return (
        <div className="mb-3">
          <label className="form-label">Ad</label>
          <input
            type="text"
            className="form-control"
            value={formState.name ?? ''}
            onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
          />
        </div>
      );
    }
    if (key === 'ListingComplaintProcess') {
      return (
        <div className="mb-3">
          <label className="form-label">Metin</label>
          <input
            type="text"
            className="form-control"
            value={formState.text ?? ''}
            onChange={(e) => setFormState((s) => ({ ...s, text: e.target.value }))}
          />
        </div>
      );
    }
    if (key === 'Policy') {
      return (
        <>
          <div className="mb-3">
            <label className="form-label">Ad</label>
            <input
              type="text"
              className="form-control"
              value={formState.name ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Slug</label>
            <input
              type="text"
              className="form-control"
              value={formState.slug ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, slug: e.target.value }))}
              placeholder="örn: terms-of-service, privacy-policy, listing-agreement"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Metin</label>
            <PolicyRichTextEditor
              value={formState.text ?? ''}
              onChange={(html) => setFormState((s) => ({ ...s, text: html }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Etkilenen roller</label>
            <p className="form-text mb-2">
              Boş bırakılırsa bireysel ve kurumsal üyeler için geçerlidir. Yönetici hesapları sözleşme onayından muaftır.
            </p>
            {assignablePolicyRoles.map((role) => {
              const checked = (formState.affectedRoles ?? []).includes(role);
              return (
                <div className="form-check" key={role}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`policy-role-${role}`}
                    checked={checked}
                    onChange={(e) => {
                      setFormState((s) => {
                        const current = s.affectedRoles ?? [];
                        const next = e.target.checked
                          ? [...current, role]
                          : current.filter((r) => r !== role);
                        return { ...s, affectedRoles: next };
                      });
                    }}
                  />
                  <label className="form-check-label" htmlFor={`policy-role-${role}`}>
                    {POLICY_ROLE_LABELS[role] ?? role}
                  </label>
                </div>
              );
            })}
          </div>
        </>
      );
    }
    if (key === 'NotificationType') {
      return (
        <>
          <div className="mb-3">
            <label className="form-label">Başlık</label>
            <input
              type="text"
              className="form-control"
              value={formState.title ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, title: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Mesaj</label>
            <input
              type="text"
              className="form-control"
              value={formState.message ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, message: e.target.value }))}
            />
          </div>
        </>
      );
    }
    if (key === 'District') {
      return (
        <>
          <div className="mb-3">
            <label className="form-label">Ad</label>
            <input
              type="text"
              className="form-control"
              value={formState.name ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Şehir</label>
            <select
              className="form-select"
              value={formState.cityId ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, cityId: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">Seçin</option>
              {cities.map((c) => (
                <option key={c.id} value={c.id}>{c.name ?? c.id}</option>
              ))}
            </select>
          </div>
        </>
      );
    }
    if (key === 'Neighborhood') {
      return (
        <>
          <div className="mb-3">
            <label className="form-label">Ad</label>
            <input
              type="text"
              className="form-control"
              value={formState.name ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">İlçe</label>
            <select
              className="form-select"
              value={formState.districtId ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, districtId: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">Seçin</option>
              {districts.map((d) => (
                <option key={d.id} value={d.id}>{d.name ?? d.id}</option>
              ))}
            </select>
          </div>
        </>
      );
    }
    if (key === 'SecondaryCategory') {
      return (
        <>
          <div className="mb-3">
            <label className="form-label">Ad</label>
            <input
              type="text"
              className="form-control"
              value={formState.name ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Üst Kategori</label>
            <select
              className="form-select"
              value={formState.topCategoryId ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, topCategoryId: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">Seçin</option>
              {topCategories.map((c) => (
                <option key={c.id} value={c.id}>{c.name ?? c.id}</option>
              ))}
            </select>
          </div>
        </>
      );
    }
    if (key === 'ProductType') {
      return (
        <>
          <div className="mb-3">
            <label className="form-label">Ad</label>
            <input
              type="text"
              className="form-control"
              value={formState.name ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Alt Kategori</label>
            <select
              className="form-select"
              value={formState.secondaryCategoryId ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, secondaryCategoryId: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">Seçin</option>
              {secondaryCategories.map((c) => (
                <option key={c.id} value={c.id}>{c.name ?? c.id}</option>
              ))}
            </select>
          </div>
        </>
      );
    }
    if (key === 'BrandCategory') {
      return (
        <>
          <div className="mb-3">
            <label className="form-label">Marka</label>
            <select
              className="form-select"
              value={formState.brandId ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, brandId: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">Seçin</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>{b.name ?? b.id}</option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Ürün Tipi</label>
            <select
              className="form-select"
              value={formState.productTypeId ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, productTypeId: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">Seçin</option>
              {productTypes.map((p) => (
                <option key={p.id} value={p.id}>{p.name ?? p.id}</option>
              ))}
            </select>
          </div>
        </>
      );
    }
    if (key === 'SpecConstraint') {
      const allowedRows = formState.allowedValues ?? [''];
      const normalizedValueType = normalizeSpecValueType(formState.valueType ?? SpecValueType.Number);
      return (
        <>
          <div className="mb-3">
            <label className="form-label">Ürün Tipi</label>
            <select
              className="form-select"
              value={formState.productTypeId ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, productTypeId: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">Seçin</option>
              {productTypes.map((p) => (
                <option key={p.id} value={p.id}>{p.name ?? p.id}</option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Anahtar</label>
            <input
              type="text"
              className="form-control"
              value={formState.specKey ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, specKey: e.target.value }))}
              placeholder="örn: ram, cpu, panel_type"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Görünen Ad</label>
            <input
              type="text"
              className="form-control"
              value={formState.specDisplayName ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, specDisplayName: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Değer Tipi</label>
            <select
              className="form-select"
              value={normalizedValueType}
              onChange={(e) => {
                const nextType = Number(e.target.value) as SpecValueType;
                setFormState((s) => ({
                  ...s,
                  valueType: nextType,
                  allowedValues: nextType === SpecValueType.Enum ? (s.allowedValues?.length ? s.allowedValues : ['']) : [],
                }));
              }}
            >
              <option value={SpecValueType.Number}>Sayı</option>
              <option value={SpecValueType.String}>Metin</option>
              <option value={SpecValueType.Enum}>Seçenek listesi</option>
            </select>
          </div>
          {normalizedValueType === SpecValueType.Enum && (
            <div className="mb-3">
              <label className="form-label">İzin Verilen Değerler</label>
              <p className="text-muted small mb-2">Sıralamak için değerleri sürükleyip bırakın.</p>
              {allowedRows.map((val, idx) => (
                <div
                  key={`allowed-${idx}`}
                  className="input-group mb-2"
                  draggable
                  onDragStart={() => setAllowedValueDragIndex(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (allowedValueDragIndex == null || allowedValueDragIndex === idx) return;
                    const next = [...allowedRows];
                    const [moved] = next.splice(allowedValueDragIndex, 1);
                    next.splice(idx, 0, moved);
                    setFormState((s) => ({ ...s, allowedValues: next }));
                    setAllowedValueDragIndex(null);
                  }}
                  style={{ cursor: 'grab' }}
                >
                  <span className="input-group-text">
                    <i className="fas fa-grip-vertical text-muted" aria-hidden="true" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    value={val}
                    onChange={(e) => {
                      const next = [...allowedRows];
                      next[idx] = e.target.value;
                      setFormState((s) => ({ ...s, allowedValues: next }));
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => {
                      const next = allowedRows.filter((_, i) => i !== idx);
                      setFormState((s) => ({ ...s, allowedValues: next.length > 0 ? next : [''] }));
                    }}
                    aria-label="Değeri kaldır"
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setFormState((s) => ({ ...s, allowedValues: [...(s.allowedValues ?? ['']), ''] }))}
              >
                <i className="fas fa-plus me-1" />
                Değer Ekle
              </button>
            </div>
          )}
        </>
      );
    }
    return null;
  };

  return (
    <>
      <ProductTypeRuleModal
        show={ruleModalOpen}
        onClose={() => setRuleModalOpen(false)}
        productTypes={productTypes}
      />
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
        <h1 className="h2">Veri Yönetimi</h1>
      </div>
      <div className="card">
        <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
          <select
            className="form-select form-select-sm w-auto"
            value={entityKey}
            onChange={(e) => handleEntityKeyChange(e.target.value as EntityKey)}
          >
            {(Object.keys(ENTITY_LABELS) as EntityKey[]).map((key) => (
              <option key={key} value={key}>
                {ENTITY_LABELS[key]}
              </option>
            ))}
          </select>
          <div className="d-flex align-items-center gap-2">
            {entityKey === 'ProductType' && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setRuleModalOpen(true)}
                aria-label="Özellik kurallarını yönet"
                title="Özellik kurallarını yönet"
              >
                <i className="fas fa-cog" aria-hidden="true" />
              </button>
            )}
            {showPaginatedControls && (
              <button
                type="button"
                className={`btn btn-sm sb-data-management-filter-toggle ${
                  filtersExpanded || hasActiveFilters ? 'btn-outline-primary' : 'btn-outline-secondary'
                }`}
                onClick={() => setFiltersExpanded((expanded) => !expanded)}
                aria-expanded={filtersExpanded}
                aria-controls="dm-filter-panel"
                aria-label={filtersExpanded ? 'Filtreleri gizle' : 'Filtreleri göster'}
                title={filtersExpanded ? 'Filtreleri gizle' : 'Filtreleri göster'}
              >
                <i className="fas fa-filter" aria-hidden="true" />
                {!filtersExpanded && activeFilterCount > 0 && (
                  <span className="badge rounded-pill bg-primary ms-1">{activeFilterCount}</span>
                )}
              </button>
            )}
            <button type="button" className="btn btn-primary btn-sm" onClick={handleAddClick}>
              <i className="fas fa-plus me-1" />
              Yeni Ekle
            </button>
          </div>
        </div>
        <div className="card-body p-0">
          {listError && (
            <div className="alert alert-danger m-3 mb-0 py-2" role="alert">
              {listError}
            </div>
          )}
          {showPaginatedControls && (
            <DataManagementFilters
              entityKey={entityKey as PaginatedDataEntityKey}
              searchText={searchText}
              onSearchTextChange={setSearchText}
              parentFilterId={parentFilterId}
              onParentFilterIdChange={(value) => {
                setParentFilterId(value);
                setPageNumber(1);
              }}
              parentFilterOptions={parentFilterOptions}
              sort={sort}
              onSortChange={(value) => {
                setSort(value);
                setPageNumber(1);
              }}
              expanded={filtersExpanded}
              onExpand={() => setFiltersExpanded(true)}
            />
          )}
          {loading ? (
            <div className="d-flex align-items-center justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center text-muted py-5">Kayıt bulunamadı.</div>
          ) : (
            <div className="sb-data-management-list">
              <EntityList
                items={items}
                getDisplayName={getDisplayName}
                onUpdate={handleUpdateClick}
                onDelete={handleDelete}
              />
            </div>
          )}
          {showPaginatedControls && !loading && (
            <DataManagementPagination
              pageNumber={pageNumber}
              pageSize={pageSize}
              totalPages={totalPages}
              onPageChange={setPageNumber}
              onPageSizeChange={(nextPageSize) => {
                setPageSize(nextPageSize);
                setPageNumber(1);
              }}
            />
          )}
        </div>
      </div>
      {(SOFT_DELETABLE_ENTITIES as readonly EntityKey[]).includes(entityKey) && !loading && deletedItems.length > 0 && (
        <div className="card mt-3 border-secondary sb-data-management-deleted">
          <div className="card-header">
            <h2 className="h6 mb-0 text-muted">Silinmiş {ENTITY_LABELS[entityKey]}</h2>
          </div>
          <div className="card-body p-0 sb-data-management-list">
            <EntityList
              items={deletedItems}
              getDisplayName={getDisplayName}
              hideActions
            />
          </div>
        </div>
      )}
      <FormModal
        title={editingItem ? `${ENTITY_LABELS[entityKey]} Güncelle` : `Yeni ${ENTITY_LABELS[entityKey]}`}
        show={showForm}
        onClose={() => setShowForm(false)}
        onAccept={handleFormAccept}
        acceptLabel={editingItem ? 'Güncelle' : 'Ekle'}
      >
        {renderFormFields()}
        {submitError && (
          <div className="alert alert-danger py-2" role="alert">
            {submitError}
          </div>
        )}
      </FormModal>
    </>
  );
};

export default DataManagementPage;
